import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Dimension, Product } from '@inter-ikea-storageone/range';
import {
  filterApplied,
  getFilterState,
  getProductsForGroup,
  getSelectedGroupId
} from 'ducks/products';
import { useAppDispatch } from 'ducks/hooks';
import SizeToggle from 'ui/SizeToggle/SizeToggle';
import { getFilterOptions, getUnit } from 'filters/SizeFilter/utils';
import Divider from 'ui/Divider/Divider';

const StyledDivider = styled(Divider)`
  margin-top: 24px;
`;

const ChangeSizeFilter = ({
  dimension,
  displayDivider
}: {
  dimension: Dimension;
  displayDivider?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const productsInGroup = useSelector(getProductsForGroup);
  const filterState = useSelector(getFilterState);
  const selectedGroupId = useSelector(getSelectedGroupId);

  const unit = getUnit(productsInGroup);

  const designFilterState = useMemo(() => filterState(selectedGroupId, 'design'), [
    filterState,
    selectedGroupId
  ]);

  const availableSizes = getFilterOptions(dimension)(productsInGroup);
  const sizes = Object.entries(availableSizes).map(([size, communicated]) => ({
    value: parseInt(size, 10),
    text: `${communicated}`,
    unit: `${unit}`
  }));

  const [selectedSize, setSelectedSize] = useState(sizes[0].value);

  const filterProducts = useCallback(
    (product: Product, dimension: Dimension, size: number) => {
      const { design, height, depth } = designFilterState;
      let dimensionsMatch = false;

      if (dimension === 'depth') {
        dimensionsMatch =
          product.communicatedDimensions[dimension] === size &&
          product.communicatedDimensions.height === height;
      } else if (dimension === 'height') {
        dimensionsMatch =
          product.communicatedDimensions[dimension] === size &&
          product.communicatedDimensions.depth === depth;
      }

      return design ? dimensionsMatch && product.design === design : dimensionsMatch;
    },
    [designFilterState]
  );

  const onChangeSize = useCallback(
    (size: number) => {
      setSelectedSize(size);

      if (designFilterState[dimension] !== size) {
        const newFilterState = { ...designFilterState, [dimension]: size };

        const filteredProducts = productsInGroup
          .filter(p => filterProducts(p, dimension, size))
          .map(p => p.coreProductId);

        dispatch(
          filterApplied({
            id: 'design',
            productGroupId: selectedGroupId,
            productIdList: filteredProducts,
            filterState: newFilterState
          })
        );
      }
    },
    [dispatch, filterProducts, designFilterState, selectedGroupId, productsInGroup, dimension]
  );

  useEffect(() => {
    if (!designFilterState[dimension]) {
      onChangeSize(sizes[0].value);
    } else {
      setSelectedSize(designFilterState[dimension]);
    }
  }, [onChangeSize, designFilterState, sizes, dimension]);

  return (
    <>
      <SizeToggle
        dataTestId={`change-${dimension}-filter`}
        title={formatMessage({ id: `storageone.${dimension}` })}
        currentSize={selectedSize}
        sizes={sizes}
        onChangeSize={onChangeSize}
      />
      {displayDivider && <StyledDivider />}
    </>
  );
};

export default ChangeSizeFilter;

export const ChangeDepthFilter = () => {
  return <ChangeSizeFilter dimension="depth" />;
};

export const ChangeHeightFilter = () => {
  return <ChangeSizeFilter dimension="height" displayDivider />;
};
